import { CreateMonitorDTO, MonitorDTO, UpdateMonitorDTO } from '@meshwatch/types';

import { DynamoDBClient } from '../../shared';
import { DEFAULT_DYNAMO_DB_CLIENT } from '../../shared/aws/dynamodb';
import { BadRequestException, ForbiddenException, validationBoom } from '../../shared/rest';
import BaseService, { ServiceResponse } from '../../shared/services';
import Authorization from '../../shared/services/access';
import { FormErrorsBoom } from '../../shared/validation/types';

import { MonitorDatasource, DynamoMonitorDatasource } from './db';
import { validateCreateMonitorPayload, validateUpdateMonitorPayload } from './validation';

export class MonitorsService extends BaseService {
  private readonly datasource: MonitorDatasource;

  public constructor(dynamoClient: DynamoDBClient = DEFAULT_DYNAMO_DB_CLIENT) {
    super();
    this.datasource = new DynamoMonitorDatasource(dynamoClient);
  }

  public bookmarkMonitor = async (
    userId: string,
    monitorId: string
  ): Promise<ServiceResponse<MonitorDTO | FormErrorsBoom<undefined>>> => {
    return this.tryExecute(async () => {
      let monitor = await this.datasource.getMonitor(userId, monitorId);
      monitor = await this.datasource.bookmarkMonitor(
        monitor.userId,
        monitor.id,
        !monitor.isBookmarked
      );
      return this.serviceResponse(monitor);
    });
  };

  public getMonitor = async (
    userId: string,
    monitorId: string
  ): Promise<ServiceResponse<MonitorDTO | FormErrorsBoom<undefined>>> => {
    return this.tryExecute(async () => {
      const monitor = await this.datasource.getMonitor(userId, monitorId);
      if (Authorization.of(userId).cannotAccessEntity(monitor)) {
        throw new ForbiddenException('You are not authorized to perform this action');
      }
      return this.serviceResponse(monitor);
    });
  };

  public deleteMonitor = async (
    userId: string,
    monitorId: string
  ): Promise<ServiceResponse<MonitorDTO | FormErrorsBoom<undefined>>> => {
    return this.tryExecute(async () => {
      const monitor = await this.datasource.getMonitor(userId, monitorId);
      await this.datasource.deleteMonitor(monitor.userId, monitor.id);
      return this.serviceResponse(monitor);
    });
  };

  public getMonitorsByScheduler = async (
    scheduler: string
  ): Promise<ServiceResponse<MonitorDTO[] | FormErrorsBoom<undefined>>> => {
    return this.tryExecute(async () => {
      const monitors = await this.datasource.getMonitorsByScheduler(scheduler);
      return this.serviceResponse(monitors);
    });
  };

  public getMonitors = async (
    userId: string
  ): Promise<ServiceResponse<MonitorDTO[] | FormErrorsBoom<undefined>>> => {
    return this.tryExecute(async () => {
      const monitors = await this.datasource.searchMonitors(userId);
      return this.serviceResponse(monitors);
    });
  };

  public updateMonitor = async (userId: string, updateMonitorPayload: UpdateMonitorDTO) => {
    const validationResponse = await validateUpdateMonitorPayload(updateMonitorPayload);
    if (validationResponse.error) {
      return this.serviceResponseFromBoom(validationBoom(validationResponse.errors));
    }
    return this.tryExecute(async () => {
      if (Authorization.of(userId).cannotAccessEntity(updateMonitorPayload)) {
        throw new ForbiddenException('You are not authorized to perform this action');
      }

      const existingMonitor = await this.datasource.getMonitor(userId, updateMonitorPayload.id);
      if (existingMonitor.type !== updateMonitorPayload.type) {
        throw new BadRequestException(
          'Monitor type cannot be changes. Please create a new monitor.'
        );
      }

      const monitor = await this.datasource.updateMonitor({
        ...updateMonitorPayload,
        created: existingMonitor.created,
      });
      return { statusCode: 200, body: monitor };
    });
  };

  public createMonitor = async (userId: string, newMonitorPayload: CreateMonitorDTO) => {
    const validationResponse = await validateCreateMonitorPayload(newMonitorPayload);
    if (validationResponse.error) {
      return this.serviceResponseFromBoom(validationBoom(validationResponse.errors));
    }
    return this.tryExecute(async () => {
      if (Authorization.of(userId).cannotAccessEntity(newMonitorPayload)) {
        throw new ForbiddenException('You are not authorized to perform this action');
      }

      const monitor = await this.datasource.createMonitor(newMonitorPayload);

      return { statusCode: 201, body: monitor };
    });
  };
}

const monitorsService = new MonitorsService();

export default monitorsService;
